import { FC } from 'react'
import { GRAY_700, GRAY_900, MOBILE_VIEW_WIDTH_QUERY } from 'constants/styling/theme'
import { MUIButton } from 'components/common/MUIButton'
import Modal from 'components/common/Modals/Modal/Modal'
import { ResponsiveDrawer } from 'components/common/ResponsiveDrawer'
import Stack from '@mui/material/Stack'
import { StarRatingForm } from '../StarRatingForm'
import { StarRatingThankYou } from '../StarRatingThankYou'
import Typography from '@mui/material/Typography'
import { UserAvatarChip } from 'components/common/UserAvatarChip'
import { StarRatingPopupStep, useStarRatingPopup } from './StarRatingPopup.context'
import useMediaQuery from '@mui/material/useMediaQuery'
import { Trans, useTranslation } from 'react-i18next'

interface Props {
  isOpen: boolean
  onClose: () => void
}

/**
 * Controller for the StarRatingPopup.
 * Renders a Modal with step-based content (rating form -> thank you).
 *
 * DEPENDENCIES:
 * - StarRatingPopupContext
 */
export const StarRatingPopupController: FC<Props> = ({
  isOpen,
  onClose
}) => {
  const { t } = useTranslation(['star_rating', 'common'])
  const isMobileView = useMediaQuery(MOBILE_VIEW_WIDTH_QUERY)

  const {
    step,
    isUpdate,
    isSubmitting,
    canSubmit,
    hasCreative,
    creativeName,
    creativeAvatarUrl,
    responseAvgRating,
    submit,
    buildEntries,
    onComplete
  } = useStarRatingPopup()

  const handleClose = () => {
    if (step === StarRatingPopupStep.THANK_YOU) {
      onComplete?.(buildEntries(), responseAvgRating)
    }
    onClose()
  }

  const footerContent = (
    <>

      {step === StarRatingPopupStep.RATING &&
        <Stack direction="row" justifyContent="flex-end" gap="1.2rem" width="100%">

          <MUIButton
            type="secondaryBorder"
            onClick={handleClose}
          >
            {t('common:Cancel')}
          </MUIButton>

          <MUIButton
            isLoading={isSubmitting}
            disabled={!canSubmit}
            onClick={submit}
          >
            {isUpdate ? t('star_rating:update') : t('star_rating:send')}
          </MUIButton>

        </Stack>
      }

      {step === StarRatingPopupStep.THANK_YOU &&
        <Stack direction="row" justifyContent="flex-end" width="100%">
          <MUIButton onClick={handleClose}>
            {t('star_rating:close')}
          </MUIButton>
        </Stack>
      }

    </>
  )

  const titleContent = step === StarRatingPopupStep.RATING
    ? (
      <Typography variant="text-lg" fontWeight={600} color={GRAY_900}>
        {t('star_rating:title')}
      </Typography>
    )
    : undefined

  const subtitleContent = step === StarRatingPopupStep.RATING
    ? (
      <Typography variant="text-sm" color={GRAY_700} component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '.4rem', flexWrap: 'wrap' }}>
        {hasCreative && creativeName
          ? (
            <Trans
              t={t}
              i18nKey="star_rating:subtitle_with_ct"
              components={{
                chip: <UserAvatarChip name={creativeName} avatarUrl={creativeAvatarUrl} />,
              }}
            />
          )
          : t('star_rating:subtitle')
        }
      </Typography>
    )
    : undefined

  const formContent = (
    <Stack
      width={isMobileView ? '100%' : '52rem'}
      maxWidth="100%"
      padding={isMobileView ? '0' : '2.4rem 1.2rem 2.4rem 0'}
      gap="2rem"
    >

      {step === StarRatingPopupStep.RATING &&
        <StarRatingForm />
      }

      {step === StarRatingPopupStep.THANK_YOU &&
        <StarRatingThankYou />
      }

    </Stack>
  )

  if (isMobileView) {
    return (
      <ResponsiveDrawer
        isOpen={isOpen}
        onClose={handleClose}
        title={step === StarRatingPopupStep.RATING ? t('star_rating:title') : t('star_rating:thank_you_title')}
        subTitleSlot={subtitleContent}
        actionsSlot={footerContent}
      >
        {formContent}
      </ResponsiveDrawer>
    )
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      onClickOutside={step === StarRatingPopupStep.RATING ? handleClose : undefined}
      title={titleContent}
      subtitle={subtitleContent}
      hasHeaderContentDivider={step === StarRatingPopupStep.RATING}
      footerContent={footerContent}
    >
      {formContent}
    </Modal>
  )
}
